import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  usePostTargets,
  usePostTargetsTestConnection,
  useGetAgents,
  getProfilesById,
  getGetProfilesByIdQueryKey,
} from '@/api/control/control-plane/control-plane';
import type {
  TestAzureSqlConnectionRequest,
  SourceDto,
} from '@/api/control/missionImpactAPI.schemas';
import { WizardStepper } from '@/components/profiles/wizard/WizardStepper';
import { getWizardSteps, WIZARD_STEP_IDS } from '@/components/profiles/wizard/wizardSteps';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { AlertCircle, Info, Loader2 } from 'lucide-react';
import { ConnectionTestAlert } from '@/components/common/ConnectionTestAlert';
import { LoadingButton } from '@/components/LoadingButton';
import { logger } from '@/services/logger';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { useError } from '@/contexts/ErrorContext';
import { FirewallHelpDialog } from '@/components/common/FirewallHelpDialog';
import { isValidTablePrefix } from '@/lib/tablePrefix';
import { AdvancedSettingsSection } from '@/components/profiles/settings/AdvancedSettingsSection';

/** Auth type discriminator matching backend $type values */
type AuthType = 'AzureSql' | 'AzureSqlCredential';

/**
 * Step 3: Add Azure SQL Target
 * Third step in the profile creation wizard
 */
export default function AddTargetStep() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useLanguage();
  const { handleApiError } = useError();
  const { orgId, profileId } = useParams<{ orgId: string; profileId: string }>();

  if (!orgId || !profileId) {
    logger.error(new Error('Missing orgId or profileId in route params'), {
      context: 'AddTargetStep.mount',
    });
    void navigate('/dashboard');
    return null;
  }

  const wizardSteps = getWizardSteps(WIZARD_STEP_IDS.TARGET);

  // Auth type state
  const [authType, setAuthType] = useState<AuthType>('AzureSql');

  // Service principal fields
  const [useSourceCredentials, setUseSourceCredentials] = useState(false);
  const [tenantId, setTenantId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  // SQL credential fields
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [encryptConnection, setEncryptConnection] = useState(true);

  // Shared fields
  const [server, setServer] = useState('');
  const [database, setDatabase] = useState('');
  const [schema, setSchema] = useState('dbo');
  const [tablePrefix, setTablePrefix] = useState('');
  const [agentId, setAgentId] = useState<string>('none');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [connectionTestSuccess, setConnectionTestSuccess] = useState(false);
  const [connectionTestMessage, setConnectionTestMessage] = useState<string | null>(null);
  const [connectionTestError, setConnectionTestError] = useState<string | null>(null);
  const [firewallDialogOpen, setFirewallDialogOpen] = useState(false);

  // Fetch agents for agent selector
  const { data: agentsResponse } = useGetAgents(orgId);
  const agents = agentsResponse?.data ?? [];

  // Fetch profile to verify it exists and has a source
  const { data: profileData, isLoading: isLoadingProfile } = useQuery({
    queryKey: getGetProfilesByIdQueryKey(orgId, profileId),
    queryFn: async () => {
      const response = await getProfilesById(orgId, profileId);
      return response;
    },
    retry: 1,
  });

  const source: SourceDto | undefined = profileData?.data.source ?? undefined;

  /**
   * Resets connection test state
   */
  const resetConnectionTest = () => {
    setConnectionTestSuccess(false);
    setConnectionTestMessage(null);
    setConnectionTestError(null);
  };

  // Handle checkbox change - populate clientId from source when checked
  useEffect(() => {
    if (useSourceCredentials && source?.clientId) {
      setClientId(source.clientId);
      resetConnectionTest();
    } else if (!useSourceCredentials) {
      setClientId('');
      resetConnectionTest();
    }
  }, [useSourceCredentials, source?.clientId]);

  // Redirect if profile doesn't exist, doesn't have a source, or if target already exists (auto-skip)
  useEffect(() => {
    if (!isLoadingProfile && profileData?.data) {
      if (!profileData.data.source) {
        void navigate(`/dashboard/orgs/${orgId}/profiles/${profileId}/setup/source`);
      } else if (profileData.data.target) {
        void navigate(`/dashboard/orgs/${orgId}/profiles/${profileId}/setup/tables`);
      }
    } else if (!isLoadingProfile && !profileData?.data) {
      void navigate(`/dashboard/orgs/${orgId}/profiles/wizard/profile`);
    }
  }, [isLoadingProfile, profileData, navigate, orgId, profileId]);

  // Whether a self-hosted agent is selected (SQL credentials only allowed with an agent)
  const hasAgentSelected = !!agentId && agentId !== 'none';

  /**
   * Handles agent selection change - resets auth type to Service Principal when agent is deselected
   */
  const handleAgentChange = (value: string) => {
    setAgentId(value);
    resetConnectionTest();
    if (!value || value === 'none') {
      setAuthType('AzureSql');
      setUsername('');
      setPassword('');
    }
  };

  /**
   * Handles auth type change, clearing credential fields and resetting test state
   */
  const handleAuthTypeChange = (value: AuthType) => {
    setAuthType(value);
    setUseSourceCredentials(false);
    setTenantId('');
    setClientId('');
    setClientSecret('');
    setUsername('');
    setPassword('');
    setEncryptConnection(true);
    resetConnectionTest();
  };

  /**
   * Whether all required credential fields are filled for the current auth type
   */
  const areCredentialsFilled = authType === 'AzureSql'
    ? tenantId.trim() && clientId.trim() && clientSecret.trim()
    : username.trim() && password.trim();

  /**
   * Whether all required fields (credentials + connection) are filled
   */
  const areAllFieldsFilled =
    areCredentialsFilled && server.trim() && database.trim() && schema.trim();

  // Test connection mutation
  const { mutate: testConnection, isPending: isTesting } = usePostTargetsTestConnection({
    mutation: {
      onSuccess: () => {
        setConnectionTestSuccess(true);
        setConnectionTestMessage(
          t.wizard.target.connectionSuccessful
        );
        setConnectionTestError(null);
      },
      onError: (error) => {
        setConnectionTestSuccess(false);
        setConnectionTestMessage(null);
        const { message, errorCode } = handleApiError(
          error,
          { context: 'AddTargetStep.testConnection', profile: profileData?.data },
          { showToast: false },
        );

        if (errorCode === 'AzureSqlFirewallBlocked') {
          setFirewallDialogOpen(true);
        }

        setConnectionTestError(message);
      },
    },
  });

  // Create target mutation
  const { mutate: createTarget, isPending: isCreating } = usePostTargets({
    mutation: {
      onSuccess: () => {
        toast.success(t.targets.notifications.createSuccess);
        logger.trackEvent('Target.Created', {
          orgId,
          profileId,
        });

        // Invalidate queries
        void queryClient.invalidateQueries({
          queryKey: ['targets', 'by-profile', profileId],
        });
        void queryClient.invalidateQueries({
          queryKey: getGetProfilesByIdQueryKey(orgId, profileId),
        });

        // Navigate to step 4: Select Tables
        void navigate(`/dashboard/orgs/${orgId}/profiles/${profileId}/setup/tables`);
      },
      onError: (error) => {
        const { message } = handleApiError(error, { context: 'AddTargetStep.createTarget' });
        setValidationError(message);
      },
    },
  });

  const handleTestConnection = () => {
    setValidationError(null);
    setConnectionTestMessage(null);
    setConnectionTestError(null);
    setConnectionTestSuccess(false);

    if (!areAllFieldsFilled) {
      setValidationError(t.wizard.target.allFieldsRequired);
      return;
    }

    // Build the test connection payload with correct $type discriminators
    const selectedAgentId = hasAgentSelected ? agentId : undefined;
    const payload: TestAzureSqlConnectionRequest = authType === 'AzureSql'
      ? {
          $type: 'AadApp',
          tenantId,
          clientId,
          clientSecret,
          server,
          database,
          schemaName: schema,
          agentId: selectedAgentId,
        }
      : {
          $type: 'Basic',
          username,
          password,
          encryptConnection,
          server,
          database,
          schemaName: schema,
          agentId: selectedAgentId,
        };

    testConnection({
      orgId,
      profileId,
      data: payload,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    setValidationError(null);

    if (!connectionTestSuccess) {
      setValidationError(t.wizard.target.testBeforeProceeding);
      return;
    }

    if (!isValidTablePrefix(tablePrefix)) {
      setValidationError(t.wizard.target.tablePrefixInvalid);
      return;
    }

    // Empty/whitespace prefix is sent as undefined (no prefix).
    const normalizedPrefix = tablePrefix.trim() || undefined;

    // Build config based on auth type
    const config = authType === 'AzureSql'
      ? {
          $type: 'AadApp' as const,
          tenantId,
          clientId,
          clientSecret,
          server,
          database,
          schemaName: schema,
          tablePrefix: normalizedPrefix,
        }
      : {
          $type: 'Basic' as const,
          username,
          password,
          encryptConnection,
          server,
          database,
          schemaName: schema,
          tablePrefix: normalizedPrefix,
        };

    const requestData = {
      config,
      ...(hasAgentSelected ? { agentId } : {}),
    };

    createTarget({ orgId, profileId, data: requestData });
  };

  if (isLoadingProfile) {
    return (
      <div className="space-y-6">
        <WizardStepper steps={wizardSteps} />
        <div className="flex justify-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Wizard Stepper */}
      <WizardStepper steps={wizardSteps} />

      {/* Page Header */}
      <div className="mx-auto text-center">
        <h1 className="text-3xl font-bold">{t.wizard.target.title}</h1>
        <p className="text-muted-foreground">{t.wizard.target.description}</p>
      </div>

      {/* Form Card */}
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>{t.wizard.target.cardTitle}</CardTitle>
          <CardDescription>{t.wizard.target.cardDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Agent Selector */}
            {agents.length > 0 && (
              <div className="space-y-2">
                <Label>{t.wizard.target.agent}</Label>
                <Select value={agentId} onValueChange={handleAgentChange}>
                  <SelectTrigger>
                    <SelectValue placeholder={t.wizard.target.agentPlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t.wizard.target.agentNone}</SelectItem>
                    {agents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t.wizard.target.agentDescription}
                </p>
              </div>
            )}

            {/* Firewall Configuration Alert - only relevant for cloud-hosted agent */}
            {!hasAgentSelected && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <div className="font-medium">{t.wizard.target.firewallTitle}</div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t.wizard.target.firewallSummary}
                  </p>
                  <div className="text-right mt-2">
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0"
                      onClick={() => { setFirewallDialogOpen(true); }}
                    >
                      {t.wizard.target.firewallLearnMore}
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <Separator />

            {/* Auth Type Selector - only available when a self-hosted agent is selected */}
            {hasAgentSelected && (
              <div className="space-y-2">
                <Label>{t.wizard.target.authType}</Label>
                <Select value={authType} onValueChange={(v) => { handleAuthTypeChange(v as AuthType); }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AzureSql">
                      {t.wizard.target.authTypeServicePrincipal}
                    </SelectItem>
                    <SelectItem value="AzureSqlCredential">
                      {t.wizard.target.authTypeSqlCredential}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {authType === 'AzureSql'
                    ? t.wizard.target.authTypeServicePrincipalDescription
                    : t.wizard.target.authTypeSqlCredentialDescription}
                </p>
              </div>
            )}

            {/* Service Principal Credentials */}
            {authType === 'AzureSql' && (
              <>
                {/* Tenant ID */}
                <div className="space-y-2">
                  <Label htmlFor="tenantId">{t.wizard.target.tenantId}</Label>
                  <Input
                    id="tenantId"
                    value={tenantId}
                    onChange={(e) => {
                      setTenantId(e.target.value);
                      resetConnectionTest();
                    }}
                    placeholder={t.wizard.target.tenantIdPlaceholder}
                    required
                  />
                </div>

                {/* Client ID */}
                <div className="space-y-2">
                  <Label htmlFor="clientId">{t.wizard.target.clientId}</Label>
                  <Input
                    id="clientId"
                    value={clientId}
                    onChange={(e) => {
                      setClientId(e.target.value);
                      resetConnectionTest();
                    }}
                    placeholder={t.wizard.target.clientIdPlaceholder}
                    required
                    disabled={useSourceCredentials}
                  />
                  {source?.clientId && (
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="useSourceCredentials"
                        checked={useSourceCredentials}
                        onCheckedChange={(checked) => { setUseSourceCredentials(checked === true); }}
                      />
                      <label
                        htmlFor="useSourceCredentials"
                        className="text-xs text-muted-foreground cursor-pointer"
                      >
                        {t.wizard.target.useSourceClientId}
                      </label>
                    </div>
                  )}
                </div>

                {/* Client Secret */}
                <div className="space-y-2">
                  <Label htmlFor="clientSecret">{t.wizard.target.clientSecret}</Label>
                  <Input
                    id="clientSecret"
                    type="password"
                    value={clientSecret}
                    onChange={(e) => {
                      setClientSecret(e.target.value);
                      resetConnectionTest();
                    }}
                    placeholder={t.wizard.target.clientSecretPlaceholder}
                    required
                  />
                </div>
              </>
            )}

            {/* SQL Credential Fields */}
            {authType === 'AzureSqlCredential' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="username">{t.wizard.target.username}</Label>
                  <Input
                    id="username"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      resetConnectionTest();
                    }}
                    placeholder={t.wizard.target.usernamePlaceholder}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    {t.wizard.target.usernameDescription}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t.wizard.target.password}</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      resetConnectionTest();
                    }}
                    placeholder={t.wizard.target.passwordPlaceholder}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    {t.wizard.target.passwordDescription}
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="encryptConnection"
                      checked={encryptConnection}
                      onCheckedChange={(checked) => {
                        setEncryptConnection(checked === true);
                        resetConnectionTest();
                      }}
                    />
                    <label
                      htmlFor="encryptConnection"
                      className="text-sm cursor-pointer"
                    >
                      {t.wizard.target.encryptConnection}
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t.wizard.target.encryptConnectionDescription}
                  </p>
                </div>
              </>
            )}

            <Separator />

            {/* Server */}
            <div className="space-y-2">
              <Label htmlFor="server">{t.wizard.target.server}</Label>
              <Input
                id="server"
                value={server}
                onChange={(e) => {
                  setServer(e.target.value);
                  resetConnectionTest();
                }}
                placeholder={t.wizard.target.serverPlaceholder}
                required
              />
            </div>

            {/* Database */}
            <div className="space-y-2">
              <Label htmlFor="database">{t.wizard.target.database}</Label>
              <Input
                id="database"
                value={database}
                onChange={(e) => {
                  setDatabase(e.target.value);
                  resetConnectionTest();
                }}
                placeholder={t.wizard.target.databasePlaceholder}
                required
              />
            </div>

            {/* Schema */}
            <div className="space-y-2">
              <Label htmlFor="schema">{t.wizard.target.schema}</Label>
              <Input
                id="schema"
                value={schema}
                onChange={(e) => {
                  setSchema(e.target.value);
                  resetConnectionTest();
                }}
                placeholder={t.wizard.target.schemaPlaceholder}
                required
              />
            </div>

            {/* Advanced (replication-impacting) per-target settings — opt-in. */}
            <AdvancedSettingsSection
              title={t.wizard.advancedSettings.title}
              description={t.wizard.advancedSettings.description}
            >
              {/* Table Prefix (optional) - does not affect the connection test */}
              <div className="space-y-2">
                <Label htmlFor="tablePrefix">{t.wizard.target.tablePrefix}</Label>
                <Input
                  id="tablePrefix"
                  value={tablePrefix}
                  onChange={(e) => { setTablePrefix(e.target.value); }}
                  placeholder={t.wizard.target.tablePrefixPlaceholder}
                />
                <p className="text-xs text-muted-foreground">
                  {t.wizard.target.tablePrefixDescription}
                </p>
              </div>
            </AdvancedSettingsSection>

            {/* Test Connection Button */}
            <LoadingButton
              type="button"
              variant="outline"
              onClick={handleTestConnection}
              disabled={!areAllFieldsFilled}
              className="w-full"
              isLoading={isTesting}
            >
              {t.wizard.target.testConnection}
            </LoadingButton>

            {/* Connection Test Result */}
            {(connectionTestSuccess || connectionTestError) && (
              <ConnectionTestAlert
                success={connectionTestSuccess}
                message={connectionTestError ?? connectionTestMessage ?? ''}
              />
            )}

            {/* Validation Error */}
            {validationError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{validationError}</AlertDescription>
              </Alert>
            )}

            {/* Actions */}
            <div className="flex justify-end pt-4">
              <LoadingButton type="submit" disabled={!connectionTestSuccess} isLoading={isCreating}>
                {t.wizard.target.next}
              </LoadingButton>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Firewall Configuration Dialog */}
      <FirewallHelpDialog
        open={firewallDialogOpen}
        onOpenChange={setFirewallDialogOpen}
        regionId={profileData?.data.regionId}
      />
    </div>
  );
}
